import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { useSession } from "@/hooks/auth-hooks";
import { noIndexMeta } from "@/lib/seo";

export const Route = createFileRoute("/invitations/$token")({
	component: AcceptInvitationPage,
	head: () => ({
		meta: [{ title: "AgentReacher | Accept invitation" }, ...noIndexMeta()],
	}),
});

function AcceptInvitationPage() {
	const { token } = Route.useParams();
	const { session } = useSession();
	const navigate = useNavigate();

	const [status, setStatus] = useState<
		"loading" | "validating" | "accepting" | "error" | "accepted"
	>("loading");
	const [error, setError] = useState<string | null>(null);
	const [workspaceName, setWorkspaceName] = useState<string | null>(null);
	const [validated, setValidated] = useState(false);

	const validate = useCallback(async () => {
		if (validated) return;
		setValidated(true);
		setStatus("validating");

		try {
			const res = await fetch(
				`/api/workspaces/invitations/accept?token=${encodeURIComponent(token)}`,
			);
			const data = await res.json();

			if (!res.ok || !data.valid) {
				setError(
					data.expired
						? "This invitation has expired. Ask the workspace owner for a new one."
						: "This invitation is invalid or has already been used.",
				);
				setStatus("error");
				return;
			}

			setWorkspaceName(data.workspaceName);

			// If logged in, auto-accept
			if (session) {
				await acceptInvite();
			} else {
				setStatus("loading");
			}
		} catch {
			setError("Failed to validate invitation. Please try again.");
			setStatus("error");
		}
	}, [token, session, validated]);

	// Trigger validation on first render
	if (!validated) {
		validate();
	}

	async function acceptInvite() {
		setStatus("accepting");
		try {
			const res = await fetch("/api/workspaces/invitations/accept", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ token }),
			});
			const data = await res.json();

			if (!res.ok) {
				setError(data.error || "Failed to accept invitation");
				setStatus("error");
				return;
			}

			setStatus("accepted");
			setTimeout(() => {
				navigate({ to: "/dashboard" });
			}, 1500);
		} catch {
			setError("Failed to accept invitation. Please try again.");
			setStatus("error");
		}
	}

	return (
		<div className="flex min-h-screen items-center justify-center bg-background p-4">
			<div className="w-full max-w-sm space-y-6 text-center">
				{status === "validating" || status === "accepting" ? (
					<>
						<div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground border-t-primary" />
						<p className="text-sm text-muted-foreground">
							{status === "validating"
								? "Validating invitation..."
								: "Accepting invitation..."}
						</p>
					</>
				) : status === "error" ? (
					<>
						<div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
							<svg
								className="h-6 w-6 text-destructive"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
								strokeWidth={2}
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M6 18L18 6M6 6l12 12"
								/>
							</svg>
						</div>
						<div>
							<h1 className="text-lg font-semibold text-foreground">
								Invitation Error
							</h1>
							<p className="mt-2 text-sm text-muted-foreground">{error}</p>
						</div>
						<Link
							to="/"
							className="inline-block text-sm text-primary hover:underline"
						>
							Go to homepage
						</Link>
					</>
				) : status === "accepted" ? (
					<>
						<div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10">
							<svg
								className="h-6 w-6 text-emerald-500"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
								strokeWidth={2}
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M5 13l4 4L19 7"
								/>
							</svg>
						</div>
						<div>
							<h1 className="text-lg font-semibold text-foreground">
								Welcome to the team!
							</h1>
							<p className="mt-2 text-sm text-muted-foreground">
								Redirecting to your dashboard...
							</p>
						</div>
					</>
				) : !session ? (
					<>
						<div>
							<h1 className="text-lg font-semibold text-foreground">
								You've been invited{" "}
								{workspaceName ? (
									<>
										to <strong>{workspaceName}</strong>
									</>
								) : null}
							</h1>
							<p className="mt-2 text-sm text-muted-foreground">
								Sign in or create an account to accept this invitation.
							</p>
						</div>
						<div className="flex flex-col gap-3">
							<Link
								to="/login"
								search={{ redirect: `/invitations/${token}` }}
								className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
							>
								Sign in
							</Link>
							<Link
								to="/signup"
								search={{ redirect: `/invitations/${token}` }}
								className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
							>
								Create account
							</Link>
						</div>
					</>
				) : (
					<>
						<div>
							<h1 className="text-lg font-semibold text-foreground">
								Join{" "}
								{workspaceName ? <strong>{workspaceName}</strong> : "workspace"}
							</h1>
							<p className="mt-2 text-sm text-muted-foreground">
								Accept the invitation to start collaborating.
							</p>
						</div>
						<button
							type="button"
							onClick={acceptInvite}
							className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
						>
							Accept invitation
						</button>
					</>
				)}
			</div>
		</div>
	);
}
